import { useState } from "react";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";
import { API, getAccessToken, clearTokens } from "@/lib/api";

export function DeleteAccountPage({ onBack, onDeleted }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const isLoggedIn = !!getAccessToken();

  const handleDelete = async (e) => {
    e.preventDefault();
    setError("");

    if (!isLoggedIn) {
      // Need to login first, then delete
      setLoading(true);
      try {
        const { data: loginData } = await axios.post(`${API}/auth/login`, { email, password });
        // Now delete with the token
        await axios.delete(`${API}/auth/account`, {
          headers: { Authorization: `Bearer ${loginData.access_token}` },
          data: { password },
        });
        clearTokens();
        setSuccess(true);
        if (onDeleted) onDeleted();
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to delete account. Check your email and password.");
      } finally {
        setLoading(false);
      }
    } else {
      // Already logged in
      if (!password) { setError("Password is required"); return; }
      setLoading(true);
      try {
        await axios.delete(`${API}/auth/account`, { data: { password } });
        clearTokens();
        setSuccess(true);
        if (onDeleted) onDeleted();
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to delete account. Check your password.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-700 mb-6 transition-colors"
          data-testid="delete-account-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to App
        </button>

        <div className="bg-white border border-zinc-200 p-6 sm:p-10">
          <h1
            className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-2 tracking-tight"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Delete Account & Data
          </h1>

          {success ? (
            <div className="mt-6 p-4 bg-green-50 border border-green-200">
              <p className="text-green-800 font-medium">Your account and all associated data have been permanently deleted.</p>
              <p className="text-green-700 text-sm mt-2">This includes your pallet types, cut lists, print history, and uploaded photos. None of this data can be recovered.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-zinc-600 mb-6">
                Permanently delete your Pallet Cut Calculator account and all associated data. This action cannot be undone.
              </p>

              <div className="p-4 bg-red-50 border border-red-200 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">The following data will be permanently deleted:</p>
                    <ul className="text-sm text-red-700 mt-2 space-y-1 list-disc pl-4">
                      <li>Your account and login credentials</li>
                      <li>All pallet type configurations</li>
                      <li>Your entire Cut List and saved items</li>
                      <li>All print history</li>
                      <li>All uploaded photos</li>
                    </ul>
                  </div>
                </div>
              </div>

              <form onSubmit={handleDelete} className="space-y-4">
                {!isLoggedIn && (
                  <div>
                    <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Email</Label>
                    <Input
                      id="email" type="email" value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your account email"
                      className="rounded-none border-zinc-300"
                      required
                      data-testid="delete-email-input"
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Password</Label>
                  <Input
                    id="password" type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password to confirm"
                    className="rounded-none border-zinc-300"
                    required
                    data-testid="delete-password-input"
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-600" data-testid="delete-error">{error}</p>
                )}

                <Button
                  type="submit" disabled={loading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white rounded-none font-semibold"
                  data-testid="delete-confirm-btn"
                >
                  {loading ? "Deleting..." : "Permanently Delete My Account"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
